import { useState } from 'react'
import { useChat } from '../contexts/ChatContext'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth0 } from '@auth0/auth0-react'

export default function SettingsModal({ isOpen, onClose }) {
  const { user } = useAuth0()
  const { userProfile, saveProfile, logout } = useChat()
  const { theme, changeTheme } = useTheme()

  const [activeSection, setActiveSection] = useState('general')
  const [profileForm, setProfileForm] = useState({
    name: userProfile?.name || user?.name || '',
    email: user?.email || '',
    preferences: userProfile?.answer_preferences || '',
    profession: userProfile?.profession || ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  if (!isOpen) return null

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess(false)

    if (!profileForm.name.trim()) {
      setError('يرجى إدخال الاسم')
      return
    }

    if (!profileForm.profession) {
      setError('يرجى اختيار المهنة')
      return
    }

    setSaving(true)
    try {
      await saveProfile({
        name: profileForm.name,
        answer_preferences: profileForm.preferences,
        profession: profileForm.profession
      })
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        onClose()
      }, 1000)
    } catch (err) {
      setError('فشل حفظ الملف الشخصي. حاول مرة أخرى.')
    } finally {
      setSaving(false)
    }
  }

  // Update form when userProfile changes
  useState(() => {
    if (userProfile) {
      setProfileForm({
        name: userProfile.name || user?.name || '',
        email: user?.email || '',
        preferences: userProfile.answer_preferences || '',
        profession: userProfile.profession || ''
      })
    }
  }, [userProfile, user])

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-border/50 flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          animation: 'scaleIn 300ms ease-out',
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 md:px-6 py-4 md:py-5 border-b border-border/50 bg-background/95 backdrop-blur-sm flex-shrink-0">
          <h2 className="text-xl md:text-2xl font-bold">الإعدادات</h2>
          <button
            onClick={onClose}
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col md:flex-row flex-1 min-h-0">
          {/* Sidebar Menu */}
          <div className="w-full md:w-[200px] border-b md:border-b-0 md:border-l border-border/50 p-2 md:p-3 flex-shrink-0">
            <div className="flex md:flex-col gap-2">
              <button
                onClick={() => setActiveSection('general')}
                className={`
                  flex-1 md:w-full flex items-center justify-center md:justify-start gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl transition-all duration-150 text-right
                  ${activeSection === 'general'
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'hover:bg-muted text-foreground'
                  }
                `}
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-xs md:text-sm font-medium">عام</span>
              </button>
              <button
                onClick={() => setActiveSection('profile')}
                className={`
                  flex-1 md:w-full flex items-center justify-center md:justify-start gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl transition-all duration-150 text-right
                  ${activeSection === 'profile'
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'hover:bg-muted text-foreground'
                  }
                `}
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-xs md:text-sm font-medium">الملف الشخصي</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-3 md:p-5 overflow-y-auto">
            {/* General Settings */}
            {activeSection === 'general' && (
              <div>
                <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">الإعدادات العامة</h3>

                <div>
                  <label className="block text-sm font-medium mb-2 md:mb-3">المظهر</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                    {/* Light Theme */}
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="theme"
                        value="light"
                        checked={theme === 'light'}
                        onChange={(e) => changeTheme(e.target.value)}
                        className="hidden"
                      />
                      <div
                        className={`
                          border-2 rounded-2xl px-3 py-4 md:px-4 md:py-6 text-center transition-all duration-150 cursor-pointer hover:shadow-md active:scale-[0.98]
                          ${theme === 'light'
                            ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20'
                            : 'border-border bg-card hover:border-primary/50'
                          }
                        `}
                      >
                        <div className="flex justify-center mb-2 md:mb-3">
                          <svg className="w-8 h-8 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                        <div className="text-xs md:text-sm font-semibold">فاتح</div>
                      </div>
                    </label>

                    {/* Dark Theme */}
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="theme"
                        value="dark"
                        checked={theme === 'dark'}
                        onChange={(e) => changeTheme(e.target.value)}
                        className="hidden"
                      />
                      <div
                        className={`
                          border-2 rounded-2xl px-3 py-4 md:px-4 md:py-6 text-center transition-all duration-150 cursor-pointer hover:shadow-md active:scale-[0.98]
                          ${theme === 'dark'
                            ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20'
                            : 'border-border bg-card hover:border-primary/50'
                          }
                        `}
                      >
                        <div className="flex justify-center mb-2 md:mb-3">
                          <svg className="w-8 h-8 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                          </svg>
                        </div>
                        <div className="text-xs md:text-sm font-semibold">داكن</div>
                      </div>
                    </label>

                    {/* Auto Theme */}
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="theme"
                        value="auto"
                        checked={theme === 'auto'}
                        onChange={(e) => changeTheme(e.target.value)}
                        className="hidden"
                      />
                      <div
                        className={`
                          border-2 rounded-2xl px-3 py-4 md:px-4 md:py-6 text-center transition-all duration-150 cursor-pointer hover:shadow-md active:scale-[0.98]
                          ${theme === 'auto'
                            ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20'
                            : 'border-border bg-card hover:border-primary/50'
                          }
                        `}
                      >
                        <div className="flex justify-center mb-2 md:mb-3">
                          <svg className="w-8 h-8 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                        <div className="text-xs md:text-sm font-semibold">تلقائي</div>
                      </div>
                    </label>
                  </div>
                  <small className="block mt-2 text-xs text-muted-foreground">
                    سيتم تطبيق المظهر تلقائياً حسب إعدادات نظامك في الوضع التلقائي
                  </small>
                </div>

                {/* Logout Button */}
                <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-border/50">
                  <button
                    onClick={logout}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base bg-destructive/10 text-destructive border border-destructive/30 rounded-xl hover:bg-destructive/20 active:scale-95 transition-all duration-150 font-semibold shadow-sm flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>تسجيل الخروج</span>
                  </button>
                </div>
              </div>
            )}

            {/* Profile Settings */}
            {activeSection === 'profile' && (
              <div>
                <form onSubmit={handleProfileSubmit} className="space-y-3 md:space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">الاسم</label>
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      placeholder="أدخل اسمك"
                      maxLength={200}
                      required
                      className="w-full px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base border border-input rounded-xl bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-150 shadow-sm"
                      style={{ direction: 'rtl' }}
                    />
                  </div>

                  {/* Email (Read-only) */}
                  <div>
                    <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">البريد الإلكتروني</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      disabled
                      className="w-full px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base border border-input rounded-xl bg-muted cursor-not-allowed opacity-60"
                      style={{ direction: 'rtl' }}
                    />
                    <small className="block mt-1 text-xs text-muted-foreground">
                      لا يمكن تغيير البريد الإلكتروني
                    </small>
                  </div>

                  {/* Preferences */}
                  <div>
                    <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">تفضيلات الإجابة</label>
                    <textarea
                      value={profileForm.preferences}
                      onChange={(e) => setProfileForm({ ...profileForm, preferences: e.target.value })}
                      placeholder="أدخل تفضيلاتك للإجابات (اختياري)"
                      rows={3}
                      maxLength={2000}
                      className="w-full px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base border border-input rounded-xl bg-card resize-vertical focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-150 shadow-sm"
                      style={{ direction: 'rtl' }}
                    />
                    <small className="block mt-1 text-xs text-muted-foreground">
                      سيتم استخدام هذه التفضيلات لاحقاً لتحسين الإجابات
                    </small>
                  </div>

                  {/* Profession */}
                  <div>
                    <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">المهنة</label>
                    <select
                      value={profileForm.profession}
                      onChange={(e) => setProfileForm({ ...profileForm, profession: e.target.value })}
                      required
                      className="w-full px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base border border-input rounded-xl bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-150 shadow-sm"
                      style={{ direction: 'rtl' }}
                    >
                      <option value="">اختر مهنتك</option>
                      <option value="law_student">طالب قانون</option>
                      <option value="lawyer">محامي</option>
                      <option value="jurist">موظف في بنك او في شركة</option>
                      <option value="judiciary_employee">موظف في القضاء</option>
                      <option value="notary">موثق</option>
                      <option value="ministry_employee">موظف في الوزارات والإدارة المحلية</option>
                      <option value="other">أخرى</option>
                    </select>
                  </div>

                  {/* Console Link */}
                  <div>
                    <a
                      href="https://console.ailex.aixpr.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base bg-blue-500 text-white text-center rounded-xl hover:bg-blue-600 active:scale-95 transition-all duration-150 font-semibold shadow-md flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                      <span>الذهاب إلى لوحة التحكم</span>
                    </a>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base bg-primary text-primary-foreground rounded-2xl hover:bg-primary/90 active:scale-95 transition-all duration-150 font-semibold disabled:opacity-60 shadow-md"
                    >
                      {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base bg-transparent text-foreground border border-border rounded-2xl hover:bg-muted active:scale-95 transition-all duration-150 font-semibold shadow-sm"
                    >
                      إلغاء
                    </button>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="px-3 py-2.5 md:px-4 md:py-3 bg-destructive/10 text-destructive rounded-2xl border border-destructive/30 text-xs md:text-sm shadow-sm animate-fade-in">
                      {error}
                    </div>
                  )}

                  {/* Success Message */}
                  {success && (
                    <div className="px-3 py-2.5 md:px-4 md:py-3 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-100 rounded-2xl border border-green-200 dark:border-green-800 text-xs md:text-sm shadow-sm animate-fade-in">
                      تم حفظ الملف الشخصي بنجاح
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
